import React from 'react';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';

import ExpandedRunDetails from './ExpandedRunDetails';

const RunRow = ({ controller, run, absoluteIdx, runRowKey }) => {
  const {
    expandedRun,
    getRunConfidencePercent,
    getRunPointStats,
    getRunTokenTotals,
    getRunIdentificationStats,
    getRunDataPointsHint,
    getRunFilteredAccuracy,
    formatRunDataPoints,
    toFiniteNumber,
    toFiniteCount,
    formatTokenCount,
    formatResizeDisplay,
    toBool,
    formatCostDisplay,
    loadingRunDetailsById,
    handleRunRowClick,
    handleRunExport,
  } = controller;

  const isExpanded = expandedRun === runRowKey;
  const runConfidencePercent = getRunConfidencePercent(run, runRowKey);
  const runMinConfidenceThreshold = runConfidencePercent / 100;
  const baseRunPointStats = getRunPointStats(run);
  const runTokenTotals = getRunTokenTotals(run);
  const runIdentification = getRunIdentificationStats(run);
  const runDataPointsHint = getRunDataPointsHint(run);
  const filteredRunAccuracy = getRunFilteredAccuracy(run, runMinConfidenceThreshold);
  const runDataPointsDisplay = formatRunDataPoints(run);
  const displayAvgSpeedMs = toFiniteNumber(run.avg_speed_ms ?? run.avg_speed);
  const runId = String(run?.run_id || '').trim();
  const hasLoadedRunResults = Array.isArray(run?.results);
  const hasActiveAccuracyFilter = runMinConfidenceThreshold > 0;
  const isRunDetailsLoading = Boolean(loadingRunDetailsById[runId]);
  const isAccuracyRecalcPending = hasActiveAccuracyFilter && !hasLoadedRunResults && Boolean(runId);
  const displayAvgAccuracy = filteredRunAccuracy !== null
    ? filteredRunAccuracy
    : (hasActiveAccuracyFilter && hasLoadedRunResults)
      ? null
      : (isAccuracyRecalcPending ? null : toFiniteNumber(run.avg_accuracy));
  const displayRunTotalCost = toFiniteNumber(run.total_cost);
  const canExportRun = hasLoadedRunResults ? run.results.length > 0 : Boolean(runId);
  const attempted = baseRunPointStats.attempted;
  const successful = baseRunPointStats.successful;
  const skipped = baseRunPointStats.skipped;

  return (
    <React.Fragment>
      <tr
        className="border-b border-hairline hover:bg-surface-2 transition-colors cursor-pointer"
        onClick={() => handleRunRowClick(runRowKey, run)}
      >
        <td className="p-4 text-sm text-muted">
          <div className="min-w-[86px]">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <button
                type="button"
                onClick={(event) => handleRunExport(event, run, absoluteIdx)}
                disabled={!canExportRun || isRunDetailsLoading}
                className={`inline-flex items-center rounded border px-1.5 py-1 text-[11px] font-semibold transition-colors ${(canExportRun && !isRunDetailsLoading) ? 'border-hairline bg-surface text-muted hover:bg-surface-2' : 'border-hairline bg-surface-2 text-muted/50 cursor-not-allowed'}`}
                title={!canExportRun ? 'Run details are unavailable for export.' : (hasLoadedRunResults ? 'Export this test run to CSV' : 'Load run details and export to CSV')}
                aria-label="Export run to CSV"
              >
                <Download size={12} className="mr-1" />
                {isRunDetailsLoading ? 'Loading...' : 'CSV'}
              </button>
            </div>
          </div>
        </td>
        <td className="p-4 text-sm text-muted">{new Date(run.timestamp).toLocaleString()}</td>
        <td className="p-4 text-sm text-muted">{run.model || 'N/A'}</td>
        <td className="p-4 text-sm text-muted">{formatResizeDisplay(run.resize_mode)}</td>
        <td className="p-4 text-sm text-muted">{run.compression_mode || 'N/A'}</td>
        <td className="p-4 text-sm text-muted">{toBool(run.use_ebay_rag, false) ? 'On' : 'Off'}</td>
        <td className="p-4 text-sm text-muted">{toBool(run.use_notes_rag, false) ? 'On' : 'Off'}</td>
        <td className="p-4 text-sm text-muted">{run.dataset_name || run.dataset_prefix || 'Container root'}</td>
        <td className="p-4 text-sm">
          <span className={`px-2 py-1 rounded-full text-xs font-bold ${run.mode === 'MOCK' ? 'bg-gold/10 text-gold' : 'bg-positive/10 text-positive'}`}>
            {run.mode || 'UNKNOWN'}
          </span>
        </td>
        <td className="p-4 text-sm text-muted" title={runDataPointsHint || undefined}>
          {runDataPointsDisplay}
        </td>
        <td className="p-4 text-sm text-muted">
          {displayAvgSpeedMs !== null ? `${displayAvgSpeedMs.toFixed(0)} ms` : 'N/A'}
        </td>
        <td className="p-4 text-sm font-bold text-ink">
          {isAccuracyRecalcPending ? (
            <span className="inline-flex items-center rounded bg-ai/10 px-2 py-0.5 text-[11px] font-semibold text-ai">
              Loading...
            </span>
          ) : (displayAvgAccuracy !== null ? `${(displayAvgAccuracy * 100).toFixed(1)}%` : 'N/A')}
        </td>
        {/* Both columns are percentages of lots the model got right. A name is
            "right" when its word overlap with the manifest clears 0.75, since
            a product name is usually partly right and a raw mean overlap
            reads as an accuracy it is not. Both read null on a run whose
            dataset ships no manifest, and render muted rather than as a zero. */}
        <td className="p-4 text-sm text-muted">
          <span
            className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${runIdentification.brandAccuracy !== null ? 'bg-ai/10 text-ai' : 'bg-surface-2 text-muted'}`}
          >
            {runIdentification.brandAccuracy !== null
              ? `${(runIdentification.brandAccuracy * 100).toFixed(1)}%`
              : 'N/A'}
          </span>
        </td>
        <td className="p-4 text-sm text-muted">
          <span
            className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${runIdentification.nameMatchRate !== null ? 'bg-teal/10 text-teal' : 'bg-surface-2 text-muted'}`}
          >
            {runIdentification.nameMatchRate !== null
              ? `${(runIdentification.nameMatchRate * 100).toFixed(1)}%`
              : 'N/A'}
          </span>
        </td>
        <td className="p-4 text-sm text-muted">
          <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${toFiniteCount(runTokenTotals.input) !== null ? 'bg-teal/10 text-teal' : 'bg-surface-2 text-muted'}`}>
            {formatTokenCount(runTokenTotals.input)}
          </span>
        </td>
        <td className="p-4 text-sm text-muted">
          <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${toFiniteCount(runTokenTotals.output) !== null ? 'bg-ai/10 text-ai' : 'bg-surface-2 text-muted'}`}>
            {formatTokenCount(runTokenTotals.output)}
          </span>
        </td>
        <td className="p-4 text-sm text-muted">{formatCostDisplay(displayRunTotalCost)}</td>
      </tr>
      {isExpanded && (
        <tr className="bg-surface-2 border-b border-hairline">
          <td colSpan="17" className="p-4">
            <ExpandedRunDetails
              controller={controller}
              run={run}
              runRowKey={runRowKey}
              attempted={attempted}
              successful={successful}
              skipped={skipped}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default RunRow;
